import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  type FieldValues,
  type OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  buildComputedReportRows,
  formatReportFieldValue,
} from 'features/libra/pdf/reportFieldFormatting'
import { isComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import { ConsequenceOfFailureSectionSchema } from 'features/libra/sections/consequenceOfFailureSection/consequenceOfFailureSectionTypes'
import {
  type FormSection,
  FormSectionSchema,
} from 'features/libra/sections/formSection/formSectionTypes'
import { getCofModifier } from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { parseCofFromFieldValues } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import capitalize from 'lodash/capitalize'

export interface NiiFormRow {
  label: string
  value: string
}

export interface NiiFormSectionBlock {
  title: string
  rows: ReadonlyArray<NiiFormRow>
}

/**
 * Form sections often omit `type` in workflow JSON because Zod defaults it to
 * `'form'`. Always parse through the schema so raw config matches the UI —
 * other section kinds (zones / array_table / computed) fail this parse on
 * their `type` discriminant or missing `fields`.
 */
export function parseFormSection(section: unknown): FormSection | undefined {
  const parsed = FormSectionSchema.safeParse(section)
  return parsed.success ? parsed.data : undefined
}

function buildCofRows(fieldValues: FieldValues): ReadonlyArray<NiiFormRow> {
  const { cof } = parseCofFromFieldValues(fieldValues)
  return [
    { label: 'Consequence of Failure', value: capitalize(cof.level) },
    { label: 'CoF Modifier', value: `${getCofModifier(cof.level)}×` },
    ...(cof.justification
      ? [{ label: 'CoF Justification', value: cof.justification }]
      : []),
  ]
}

/**
 * Builds PDF-ready form/computed/CoF section blocks from workflow section
 * configs. Skips zones / flowchart / array-table sections (handled elsewhere).
 */
export function buildNiiFormSectionBlocks(
  sections: ReadonlyArray<unknown>,
  ontologyFields: Record<string, OntologyField>,
  fieldValues: FieldValues
): ReadonlyArray<NiiFormSectionBlock> {
  const blocks: NiiFormSectionBlock[] = []

  for (const section of sections) {
    const formSection = parseFormSection(section)
    if (formSection) {
      // Question-list variants are rendered separately with Yes/No tags.
      if (formSection.presentation?.variant === 'question-list') continue

      const rows = formSection.fields.flatMap((fieldId) => {
        const field = ontologyFields[fieldId]
        if (field && field.category !== FieldCategory.INPUT) return []
        const raw = unwrapFieldValue(fieldValues[fieldId])
        const value = formatReportFieldValue(raw, field)
        if (value === '—') return []
        return [
          {
            label: field?.name ?? fieldId,
            value,
          },
        ]
      })
      if (rows.length === 0) continue
      blocks.push({
        title: formSection.title ?? formSection.id,
        rows,
      })
      continue
    }

    if (isComputedSection(section)) {
      const rows = buildComputedReportRows(section, ontologyFields, fieldValues)
      if (rows.length === 0) continue
      blocks.push({
        title: section.title ?? section.id,
        rows,
      })
      continue
    }

    const cofSection = ConsequenceOfFailureSectionSchema.safeParse(section)
    if (cofSection.success) {
      blocks.push({
        title: cofSection.data.title ?? 'Consequence of Failure',
        rows: buildCofRows(fieldValues),
      })
    }
  }

  return blocks
}
